import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { AppDispatch, RootState } from 'app/store';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaEdit, FaFileAlt, FaIdCard, FaTrash, FaUndo, FaUser, FaUserCircle } from 'react-icons/fa';
import { MdEmail, MdEmergency, MdLocationOn, MdPhone } from 'react-icons/md';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  clearClientState,
  ClientPayload,
  ClientState,
  createClient,
  fetchClients,
  updateClient,
  deleteClient,
  Client,
  ClientInfoFormData,
  ClientInfoFormProps
} from 'slices/NdisSlices/carporateServices/clientsManagementSlice';
import * as yup from 'yup';

const schema = yup
  .object()
  .shape({
    clientName: yup.string().required('Client name is required'),
    ndisNumber: yup.string().required('NDIS number is required'),
    email: yup.string().email('Invalid email address').required('Email is required'),
    phone: yup.string().required('Phone number is required'),
    address: yup.string().required('Address is required'),
    emergencyContactName: yup.string().optional(),
    emergencyContactPhone: yup.string().when('emergencyContactName', {
      is: (val: string) => !!val && val.length > 0,
      then: (schema) =>
        schema.required('Emergency contact phone is required when name is provided'),
      otherwise: (schema) => schema.optional()
    }),
    managerEmail: yup.string().email('Invalid manager email address').optional(),
    managerPhone: yup.string().optional(),
    avatar: yup.string().optional()
  })
  .required();

const ClientInfoForm: React.FC<ClientInfoFormProps> = ({
  defaultValues,
  onSubmit,
  onCancel,
  isEditMode
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>(defaultValues?.avatar);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue
  } = useForm<ClientInfoFormData>({
    resolver: yupResolver<ClientInfoFormData>(schema),
    defaultValues: defaultValues || {
      clientName: '',
      ndisNumber: '',
      email: '',
      phone: '',
      address: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      managerEmail: '',
      managerPhone: '',
      avatar: ''
    }
  });

  const handleReset = () => {
    reset();
    setAvatarPreview(defaultValues?.avatar);
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setValue('avatar', base64String, { shouldDirty: true });
        setAvatarPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAvatar = () => {
    setValue('avatar', '', { shouldDirty: true });
    setAvatarPreview(undefined);
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
        {isEditMode ? 'Edit Client' : 'Add New Client'}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ position: 'relative', textAlign: 'center' }}>
              <Avatar
                src={avatarPreview}
                sx={{
                  width: 96,
                  height: 96,
                  bgcolor: theme.palette.primary.light,
                  border: `2px solid ${theme.palette.primary.main}`,
                  mb: 1
                }}
              >
                {!avatarPreview && <FaUserCircle size={48} color={theme.palette.primary.main} />}
              </Avatar>
              {avatarPreview && (
                <IconButton
                  onClick={handleRemoveAvatar}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    '&:hover': {
                      bgcolor: theme.palette.error.dark
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="avatar-upload"
                type="file"
                onChange={handleAvatarChange}
              />
              <label htmlFor="avatar-upload">
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  sx={{
                    mt: 1,
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    borderWidth: 1.5,
                    '&:hover': {
                      borderWidth: 1.5
                    }
                  }}
                >
                  {avatarPreview ? 'Change Avatar' : 'Upload Avatar'}
                </Button>
              </label>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="clientName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Client's Full Name"
                  disabled={isEditMode && defaultValues?.clientName?.trim() !== ''}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaUser size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="ndisNumber"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="NDIS Number"
                  disabled={isEditMode && defaultValues?.ndisNumber?.trim() !== ''}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaIdCard size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Email Address"
                  fullWidth
                  margin="normal"
                  disabled={isEditMode && defaultValues?.email?.trim() !== ''}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdEmail size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Phone Number"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPhone size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="address"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Address"
                  multiline
                  rows={isMobile ? 2 : 3}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdLocationOn size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="emergencyContactName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Emergency Contact Name"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdEmergency size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="emergencyContactPhone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Emergency Contact Phone"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPhone size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="managerEmail"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Manager Email Address"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdEmail size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="managerPhone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Manager Phone Number"
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdPhone size={16} color={theme.palette.text.secondary} />
                      </InputAdornment>
                    )
                  }}
                  error={!!error}
                  helperText={error?.message}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderWidth: '1.5px'
                      }
                    }
                  }}
                />
              )}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            size="medium"
            onClick={handleReset}
            disabled={!isDirty}
            sx={{
              px: 3,
              py: 1,
              borderRadius: '12px',
              borderWidth: '1.5px',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderWidth: '1.5px'
              }
            }}
            startIcon={<FaUndo size={14} />}
          >
            Reset
          </Button>

          <Button
            variant="outlined"
            color="error"
            size="medium"
            onClick={onCancel}
            sx={{
              px: 3,
              py: 1,
              borderRadius: '12px',
              borderWidth: '1.5px',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderWidth: '1.5px'
              }
            }}
            startIcon={<CloseIcon fontSize="small" />}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            disabled={!isDirty}
            sx={{
              px: 4,
              py: 1,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                opacity: 0.7,
                transform: 'none'
              },
              transition: 'all 0.2s ease'
            }}
            startIcon={<FaFileAlt size={14} />}
          >
            {isEditMode ? 'Update Client' : 'Save Client'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

const ClientCard: React.FC<{ client: Client; onEdit: () => void; onDelete: () => void }> = ({
  client,
  onEdit,
  onDelete
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        border: '1px solid rgba(0,0,0,0.05)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={client.avatar}
            sx={{
              width: 64,
              height: 64,
              bgcolor: theme.palette.primary.light,
              border: `2px solid ${theme.palette.primary.main}`,
              mr: 2
            }}
          >
            {!client.avatar && <FaUserCircle size={32} color={theme.palette.primary.main} />}
          </Avatar>

          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                color: theme.palette.text.primary,
                lineHeight: 1.3,
                mb: 0.5
              }}
            >
              {client.clientName}
            </Typography>

            <Chip
              label={`NDIS: ${client.ndisNumber}`}
              size="small"
              icon={<FaIdCard size={12} />}
              sx={{
                fontSize: '0.7rem',
                height: 24,
                borderRadius: '6px',
                bgcolor: theme.palette.grey[100],
                color: theme.palette.text.secondary
              }}
            />
          </Box>
        </Box>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <MdEmail
                size={18}
                color={theme.palette.text.secondary}
                style={{ marginRight: 12, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  wordBreak: 'break-word'
                }}
              >
                {client.email}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <MdPhone
                size={18}
                color={theme.palette.text.secondary}
                style={{ marginRight: 12, flexShrink: 0 }}
              />
              <Typography variant="body2" color="text.secondary">
                {client.phone}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
              <MdLocationOn
                size={18}
                color={theme.palette.text.secondary}
                style={{ marginRight: 12, flexShrink: 0, marginTop: 2 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  wordBreak: 'break-word'
                }}
              >
                {client.address}
              </Typography>
            </Box>
          </Grid>

          {client.emergencyContactName && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: theme.palette.warning.light,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  mb: 1.5
                }}
              >
                <MdEmergency
                  size={18}
                  color={theme.palette.warning.dark}
                  style={{ marginRight: 12, flexShrink: 0, marginTop: 2 }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    display="block"
                    color={theme.palette.warning.dark}
                    sx={{ mb: 0.5 }}
                  >
                    Emergency Contact
                  </Typography>
                  <Typography
                    variant="body2"
                    color={theme.palette.warning.dark}
                    sx={{
                      wordBreak: 'break-word'
                    }}
                  >
                    {client.emergencyContactName} - {client.emergencyContactPhone}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {(client.managerEmail || client.managerPhone) && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: theme.palette.info.light,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'flex-start'
                }}
              >
                <MdEmail
                  size={18}
                  color={theme.palette.info.dark}
                  style={{ marginRight: 12, flexShrink: 0, marginTop: 2 }}
                />
                <Box>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    display="block"
                    color={theme.palette.info.dark}
                    sx={{ mb: 0.5 }}
                  >
                    Manager Contact
                  </Typography>
                  {client.managerEmail && (
                    <Typography
                      variant="body2"
                      color={theme.palette.info.dark}
                      sx={{
                        wordBreak: 'break-word',
                        mb: client.managerPhone ? 0.5 : 0
                      }}
                    >
                      Email: {client.managerEmail}
                    </Typography>
                  )}
                  {client.managerPhone && (
                    <Typography
                      variant="body2"
                      color={theme.palette.info.dark}
                      sx={{
                        wordBreak: 'break-word'
                      }}
                    >
                      Phone: {client.managerPhone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>

      <CardActions
        sx={{
          p: isMobile ? 1.5 : 2,
          pt: 0,
          justifyContent: 'flex-end',
          gap: 1
        }}
      >
        <IconButton
          onClick={onEdit}
          size="small"
          sx={{
            color: theme.palette.primary.main,
            bgcolor: theme.palette.primary.light + '33',
            '&:hover': {
              bgcolor: theme.palette.primary.light + '66'
            },
            p: 1,
            borderRadius: '8px'
          }}
        >
          <FaEdit size={16} />
        </IconButton>

        <IconButton
          onClick={onDelete}
          size="small"
          sx={{
            color: theme.palette.error.main,
            bgcolor: theme.palette.error.light + '33',
            '&:hover': {
              bgcolor: theme.palette.error.light + '66'
            },
            p: 1,
            borderRadius: '8px'
          }}
        >
          <FaTrash size={16} />
        </IconButton>
      </CardActions>
    </Card>
  );
};

const ClientManagementForm: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch<AppDispatch>();
  const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
  const { message, loading, error, clients }: ClientState = useTypedSelector(
    (state) => state.clientManagement
  );
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    dispatch(fetchClients())
      .unwrap()
      .catch((err: any) => {
        setToast({ open: true, message: err || 'Failed to fetch clients', severity: 'error' });
      });
  }, [dispatch]);

  const handleOpenModal = (client: Client | null = null) => {
    setSelectedClient(client);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedClient(null);
    dispatch(clearClientState());
  };

  const handleSubmit = (data: ClientInfoFormData) => {
    const payload: ClientPayload = {
      id: selectedClient ? selectedClient.id : '',
      group_id: '',
      service_name_id: '',
      client_email: data.email,
      client_full_name: data.clientName,
      ndis_number: data.ndisNumber,
      email_address: data.email,
      phone_number: data.phone,
      address: data.address,
      emergency_contact_name: data.emergencyContactName || '',
      emergency_contact_phone: data.emergencyContactPhone || '',
      manager_email: data.managerEmail || '',
      manager_phone: data.managerPhone || '',
      avatar: data.avatar || (selectedClient ? selectedClient.avatar : '')
    };

    const action = selectedClient ? updateClient(payload) : createClient(payload);

    dispatch(action)
      .unwrap()
      .then(() => {
        dispatch(fetchClients());
        setToast({
          open: true,
          message: selectedClient ? 'Client updated successfully!' : 'Client added successfully!',
          severity: 'success'
        });
        handleCloseModal();
      })
      .catch((err: any) => {
        setToast({
          open: true,
          message: err || (selectedClient ? 'Failed to update client' : 'Failed to create client'),
          severity: 'error'
        });
      });
  };

  const handleDelete = (email: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      dispatch(deleteClient(email))
        .unwrap()
        .then(() => {
          dispatch(fetchClients());
          setToast({ open: true, message: 'Client deleted successfully!', severity: 'success' });
        })
        .catch((err: any) => {
          setToast({ open: true, message: err || 'Failed to delete client', severity: 'error' });
        });
    }
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        sx={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 2 : 0
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{
            color: theme.palette.text.primary,
            mb: isMobile ? 0 : 0
          }}
        >
          Client Management
        </Typography>

        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenModal()}
          startIcon={<FaUser size={14} />}
          sx={{
            borderRadius: '12px',
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }
          }}
        >
          Add New Client
        </Button>
      </Stack>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Loading clients...
          </Typography>
        </Box>
      ) : clients.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
            textAlign: 'center',
            bgcolor: theme.palette.background.paper,
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: `1px dashed ${theme.palette.divider}`,
            height: '300px'
          }}
        >
          <FaUserCircle size={64} color={theme.palette.grey[400]} style={{ marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" mb={1}>
            No Clients Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Get started by adding your first client
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleOpenModal()}
            startIcon={<FaUser size={14} />}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none'
            }}
          >
            Add Client
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {clients.map((client: any) => (
            <Grid item xs={12} sm={6} lg={4} key={client.id}>
              <ClientCard
                client={client}
                onEdit={() => handleOpenModal(client)}
                onDelete={() => handleDelete(client.email)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0,0,0,0.1)'
          }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '85%', md: '70%', lg: '60%' },
            maxWidth: '800px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            borderRadius: '16px',
            overflow: 'auto',
            outline: 'none'
          }}
        >
          <ClientInfoForm
            defaultValues={selectedClient || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isEditMode={!!selectedClient}
          />
        </Box>
      </Modal>

      <Snackbar
        open={toast.open || !!message || !!error}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            top: '24px !important'
          }
        }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.open ? toast.severity : message ? 'success' : 'error'}
          sx={{
            width: '100%',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            alignItems: 'center'
          }}
          iconMapping={{
            success: <FaUserCircle size={20} />,
            error: <MdEmergency size={20} />
          }}
        >
          {toast.open ? toast.message : message || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientManagementForm;
